"use client";

import { Menu as BaseMenu } from "@base-ui/react/menu";
import { type ReactElement, type ReactNode } from "react";

function MenuRoot({ children }: { children: ReactNode }) {
  return <BaseMenu.Root>{children}</BaseMenu.Root>;
}

function MenuTrigger({
  children,
  className,
  render,
}: {
  children?: ReactNode;
  className?: string;
  render?: ReactElement;
}) {
  return (
    <BaseMenu.Trigger className={className} render={render}>
      {children}
    </BaseMenu.Trigger>
  );
}

function MenuPopup({
  children,
  align,
}: {
  children: ReactNode;
  align?: "start" | "center" | "end";
}) {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner
        className="z-50 outline-none"
        sideOffset={4}
        align={align}
      >
        <BaseMenu.Popup className="min-w-(--anchor-width) origin-(--transform-origin) rounded-lg border bg-surface p-2 shadow-[0_12px_60px_var(--black-a3),0_12px_32px_-16px_var(--gray-a5)] transition-[transform,scale,opacity] data-[ending-style]:scale-95 data-[starting-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0 dark:shadow-[0_12px_60px_var(--black-a5),0_12px_32px_-16px_var(--black-a7)]">
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

function MenuItem({
  disabled,
  onClick,
  className,
  children,
}: {
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <BaseMenu.Item
      disabled={disabled}
      onClick={onClick}
      className={`flex h-10 cursor-default select-none items-center gap-2 rounded px-4 text-sm outline-none data-highlighted:bg-primary data-highlighted:text-white ${className ?? ""}`}
    >
      {children}
    </BaseMenu.Item>
  );
}

function MenuSeparator() {
  return <BaseMenu.Separator className="mx-2 my-1.5 h-px bg-disabled" />;
}

export const Menu = {
  Root: MenuRoot,
  Trigger: MenuTrigger,
  Popup: MenuPopup,
  Item: MenuItem,
  Separator: MenuSeparator,
};
